import React, { useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';

/**
* Renders a tab content skeleton using the current theme to animate loading placeholders.
* @example
* TabContentSkeleton()
* <div className="p-6 animate-pulse"...>
* @param {{}} props - Optional props (currently unused) to allow future extensibility.
* @returns {{JSX.Element}} A themed skeleton structure mimicking tab content while data loads.
**/
export const DescriptionSkeleton = () => {
  const { theme } = useContext(ThemeContext);
  
  return (
    <div className="p-6 animate-pulse" style={{ backgroundColor: theme.background.primary }}>
      <div className="h-8 rounded mb-4" style={{ backgroundColor: theme.background.secondary, width: '60%' }} />
      <div className="flex gap-2 mb-6">
        <div className="h-6 w-16 rounded-full" style={{ backgroundColor: theme.background.secondary }} />
        <div className="h-6 w-20 rounded-full" style={{ backgroundColor: theme.background.secondary }} />
      </div>
      <div className="space-y-3 mb-6">
        <div className="h-4 rounded" style={{ backgroundColor: theme.background.secondary }} />
        <div className="h-4 rounded" style={{ backgroundColor: theme.background.secondary, width: '90%' }} />
        <div className="h-4 rounded" style={{ backgroundColor: theme.background.secondary, width: '95%' }} />
      </div>
      <div className="h-6 rounded mb-3" style={{ backgroundColor: theme.background.secondary, width: '30%' }} />
      <div className="p-4 rounded-md mb-4" style={{ backgroundColor: theme.background.secondary }}>
        <div className="h-4 rounded mb-2" style={{ backgroundColor: theme.background.tertiary, width: '40%' }} />
        <div className="h-16 rounded" style={{ backgroundColor: theme.background.tertiary }} />
      </div>
    </div>
  );
};

/**
* Renders a themed skeleton layout for tab content during loading states.
* @example
* TabContentSkeleton()
* <div className="p-6 animate-pulse">…</div>
* @param {{}} props - No props are required.
* @returns {JSX.Element} A pulse-animated skeleton for tab panels.
**/
export const SubmissionsSkeleton = () => {
  const { theme } = useContext(ThemeContext);
  
  return (
    <div className="p-6 animate-pulse" style={{ backgroundColor: theme.background.primary }}>
      <div className="h-6 rounded mb-4" style={{ backgroundColor: theme.background.secondary, width: '30%' }} />
      {[1, 2, 3].map(i => (
        <div key={i} className="p-4 rounded-md mb-3" style={{ backgroundColor: theme.background.secondary }}>
          <div className="flex justify-between mb-2">
            <div className="flex gap-2">
              <div className="h-6 w-20 rounded-full" style={{ backgroundColor: theme.background.tertiary }} />
              <div className="h-6 w-24 rounded" style={{ backgroundColor: theme.background.tertiary }} />
            </div>
            <div className="h-4 w-20 rounded" style={{ backgroundColor: theme.background.tertiary }} />
          </div>
          <div className="h-4 rounded" style={{ backgroundColor: theme.background.tertiary, width: '50%' }} />
        </div>
      ))}
    </div>
  );
};

/**
* Renders a pulsing skeleton loader for tab content using the current theme colors.
* @example
* TabContentSkeleton()
* <div className="flex items-center justify-center h-full animate-pulse">...</div>
* @returns {JSX.Element} Skeleton loader structure styled with the active theme.
**/
export const GenericTabSkeleton = () => {
  const { theme } = useContext(ThemeContext);
  
  return (
    <div className="flex items-center justify-center h-full animate-pulse" style={{ backgroundColor: theme.background.primary }}>
      <div className="text-center">
        <div className="size-16 rounded-full mx-auto mb-4" style={{ backgroundColor: theme.background.secondary }} />
        <div className="h-6 w-32 rounded mx-auto mb-2" style={{ backgroundColor: theme.background.secondary }} />
        <div className="h-4 w-48 rounded mx-auto" style={{ backgroundColor: theme.background.secondary }} />
      </div>
    </div>
  );
};
